import { useEffect, useCallback, useState, ReactNode, DetailedHTMLProps, HTMLAttributes } from 'react';
import * as runtime from 'react/jsx-runtime';
import { evaluate } from '@mdx-js/mdx';
import { MDXModule } from 'mdx/types';
import { MDXProvider } from '@mdx-js/react';
import { useSelector } from 'react-redux';
import { Article } from '@api/types/Article';
import { useGetArticleQuery } from '@api/baseApi';
import { LoadingDiv } from '@features/mdx/loading-div/LoadingDiv';
import { ErrorDiv } from '@features/mdx/error-div/ErrorDiv';
import { components } from '@features/mdx/MdxComponents';
import { MdxPageArticleIds } from '@features/mdx/MdxPageArticleIds';
import { selectLanguage } from '@store/global/selectors';



type MdxPageProps = Readonly<DetailedHTMLProps<HTMLAttributes<HTMLElement>, HTMLElement>> & {
	articleIds: MdxPageArticleIds;
	childrenBeforeMd?: ReactNode;
	children?: ReactNode;
}


export const MdxPage = (props: MdxPageProps) => {
	const { articleIds, className, childrenBeforeMd, children, ...otherProps } = props;

	const pageClassName = `md-page app-page${className ? ` ${className}` : ''}`;

	const language = useSelector(selectLanguage);
	const articleId = articleIds[language] ? articleIds[language] : articleIds.default;
	const { data, isLoading, isFetching, isError, isSuccess } = useGetArticleQuery({ id: articleId });

	const [ content, setContent ] = useState<MDXModule | null>(null);
	const [ loadContent, setLoadContent ] = useState<boolean>(false);
	
	
	useEffect(() => {
		if (data) loadMdx(data);
	}, [ data ]);

	
	const loadMdx = useCallback(async(data: Article) => {
		setLoadContent(true);
		const parsedComponents = await evaluate(data.content, {
			...runtime,
			useMDXComponents: () => components
		});
		if (parsedComponents) {
			setContent(parsedComponents);
		}
		setLoadContent(false);
	}, []);


	if (isLoading || isFetching || loadContent) return (
		<main {...otherProps} className={pageClassName}>
			<LoadingDiv />
		</main>
	)

	if (isError || (isSuccess && !content)) return (
		<main {...otherProps} className={pageClassName}>
			<ErrorDiv />
		</main>
	)
	
	return (
		<MDXProvider components={components}>
			<main {...otherProps} className={pageClassName}>
			
				{childrenBeforeMd &&
					<>{childrenBeforeMd}</>}
				
				{content?.default({})}
				
				{children &&
					<>{children}</>}
		
			</main>
		</MDXProvider>
	);
};